import { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import Lottie from 'lottie-react';
import animationData from '../../assets/Animation.json';
import animationThinkingData from '../../assets/Animation_thinking.json';

import type { ThinkingBannerKind } from "../../types";

interface AnimationProps {
  mouthOpen: number; // Sound value 0–1
  stop: boolean;
  isThinking: boolean;
  /** Shown under the avatar while thinking (voice `search` tool → "Searching..."). */
  thinkingBanner?: ThinkingBannerKind;
  hideThinkingText?: boolean;
}

export default function Animation({
  mouthOpen,
  stop = true,
  isThinking = false,
  thinkingBanner = "thinking",
  hideThinkingText = false,
}: AnimationProps) {
  const smoothed = useRef(0);

  useEffect(() => {
    const alpha = 0.6;
    smoothed.current = smoothed.current * alpha + mouthOpen * (1 - alpha);
  }, [mouthOpen]);

  const scale = 1 + smoothed.current * 0.3;
  const brightness = 1 + smoothed.current * 0.5;
  const blur = smoothed.current * 1.5;
  const size = window.matchMedia('(min-width: 640px)').matches ? 330 : 260;
  const sizeTransition = 60;
  const lottieRef = useRef<any>(null);

  useEffect(() => {
    lottieRef.current?.setSpeed(isThinking ? 2 : 1)
  }, [isThinking]);

  if (stop) {
    return (<Lottie
      animationData={animationData}
      loop
      autoplay = {false}
      style={{
        width: size,
        height: size,
      }}
    />)
  } else {
  return (
      <div
        style={{
          position: 'relative',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <motion.div
          animate={{ rotate: 360, opacity: isThinking ? [1, 0.6, 1] : 1 }}
          transition={{ repeat: Infinity, duration: 5, ease: 'linear' }}
          style={{
            width: size + mouthOpen * sizeTransition,
            height: size + mouthOpen * sizeTransition,
            scale,
            filter: `brightness(${brightness}) blur(${blur}px)`,
            transition: 'filter 0.2s ease, scale 0.2s ease',
          }}
        >
          <Lottie
            animationData={animationData}
            loop
            autoplay
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              opacity: isThinking ? 0 : 1,
              transition: 'opacity 0.3s ease',
              width: size + mouthOpen * sizeTransition,
              height: size + mouthOpen * sizeTransition,
            }}
          />

          <Lottie
            lottieRef={lottieRef}
            animationData={animationThinkingData}
            loop
            autoplay
            style={{
              opacity: isThinking ? 1 : 0,
              transition: 'opacity 0.3s ease',
              width: size + mouthOpen * sizeTransition,
              height: size + mouthOpen * sizeTransition,
            }}
          />
        </motion.div>
        {isThinking && !hideThinkingText &&(
          <motion.p
            animate={{ opacity: [1, 0.5, 1] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="text-sm text-gray-500 d-flex"
            style={{
              position: 'absolute',
              bottom: '-2vh',
              left: '50%',
              transform: 'translate(-50%, -50%)',
            }}
          >
            {thinkingBanner === "searching" ? "Searching..." : "Thinking..."}
          </motion.p>)}
      </div>
  );
  }
}
